<template>
  <div class="blog-home-container blog-sub-pages">
    <!-- Tag | Sub Page -->
    <breadcrumbs :breadcrumbs="breadcrumbs" />

    <div class="blog-subpage-header">
      <div class="blog-subpage-header-content">
        <h1>Articles with Tag: <span class="tag-name s-badge s-badge-blog-tag selected">#{{ pageData.tag }}</span></h1>
        <div class="blog-meta">
          <span class="article-count">{{ pageData.articles?.length || 0 }} articles</span>
        </div>
      </div>
    </div>

    <div class="blog-layout">
      <div class="blog-main articles-parent blog-articles">
        <div v-if="pageData.error" class="error-message">
          {{ pageData.error }}
        </div>

        <div v-else-if="!pageData.articles?.length" class="no-articles">
          No articles found with this tag
        </div>

        <div v-else class="article-grid">
          <article
            v-for="article in visibleArticles"
            :key="article.id"
            class="article-card"
          >
            <a :href="`/pulse/articles/${article.id}-${article.slug}`" class="article-image-link">
              <img
                :src="article.imageSizes['thumbnail']?.url ?? article.image"
                :alt="article.title"
                class="article-image"
              >
            </a>

            <div class="article-content">
              <div class="article-meta">
                <a
                  v-if="article.category"
                  :href="`/pulse/categories/${article.categorySlug}`"
                  class="s-badge s-badge-dark category-link"
                >
                  {{ article.category }}
                </a>
                <span class="date">{{ formatDate(article.date) }}</span>
              </div>

              <h2 class="article-title">
                <a :href="`/pulse/articles/${article.id}-${article.slug}`">
                  {{ article.title }}
                </a>
              </h2>

              <p class="article-description">{{ article.preview }}</p>

              <div class="article-tags" v-if="article.tags?.length">
                <a
                  v-for="t in article.tags"
                  :key="t.slug"
                  :href="`/pulse/tags/${t.slug}`"
                  class="tag s-badge s-badge-blog-tag"
                  :class="{ 'selected': t.slug === pageData.tag }"
                >
                  #{{ t.name }}
                </a>
              </div>
            </div>
          </article>
        </div>

        <!-- Load More Section -->
        <div
          v-if="hasMoreArticles"
          ref="loadMoreTrigger"
          class="read-more"
        >
          <s-button
            class="button-primary-dark s-button-hover-rise"
            @click="loadMore"
          >
            Show More Articles
          </s-button>
        </div>
      </div>

      <BlogSidePanel/>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useData } from 'vike-vue/useData';
import SButton from '#root/components/SButton.vue';
import BlogSubscribeForm from '#root/components/BlogSubscribeForm.vue';
import BlogSidePanel from "#root/components/BlogSidePanel.vue";
import Breadcrumbs from "#root/components/Breadcrumbs.vue";

// Get the data from Vike and make it reactive
const pageData = useData();

// State
const currentPage = ref(1);
const itemsPerPage = 9;
const loadMoreTrigger = ref(null);

// Computed
const visibleArticles = computed(() => {
  if (!pageData.articles) return [];
  const endIndex = currentPage.value * itemsPerPage;
  return pageData.articles.slice(0, endIndex);
});

const hasMoreArticles = computed(() => {
  if (!pageData.articles) return false;
  return currentPage.value * itemsPerPage < pageData.articles.length;
});

const breadcrumbs = computed(() => {
  return [
    {
      name: 'Home',
      path: '/pulse'
    },
    {
      name: "#" + pageData.tag
    }
  ];
});

// Methods
const loadMore = () => {
  currentPage.value++;
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const handleImageError = (event) => {
  event.target.src = 'https://cdn.stocksnap.io/img-thumbs/960w/architecture-building_YC9NVCLK9O.jpg';
};

// Infinite scroll
const observeLoadMore = () => {
  const observer = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting && hasMoreArticles.value) {
        loadMore();
      }
    },
    { threshold: 0.1 }
  );

  if (loadMoreTrigger.value) {
    observer.observe(loadMoreTrigger.value);
  }

  return observer;
};

let observer;

onMounted(() => {
  observer = observeLoadMore();
});

onUnmounted(() => {
  if (observer) {
    observer.disconnect();
  }
});
</script>
